import {
  useCallback,
  useEffect,
  useMemo,
  useState } from 'react';
import {
  Container,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  MenuItem,
  Chip,
  InputLabel,
  FormControl,
  Box,
  Stack,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import Select from '../components/SearchableSelect';
import { useTranslation } from 'react-i18next';
import {
  getAllPermissions,
  getAllUtenti,
  getUserPermissions,
  registerUtente,
  setUtenteActive,
  updateUserPermissions,
  updateUtente
} from '../APIs/UsersAPI';
import { getAllRuoli } from '../APIs/RolesAPI';
import SidebarLayout from '../components/sidebarLayout';
import { useSnackbar } from '../contexts/SnackBarContext';
import {
  getAllCategorie,
  getUserCategoryVisibility,
  saveUserCategoryVisibility,
} from '../APIs/CategoriesAPI';

const PERMISSION_LABELS = {
  'dashboard:view': 'Visualizzare landing page',
  'categories:manage': 'Gestire categorie',
  'categories:view': 'Vedere categorie',
  'clients:manage': 'Gestire clienti',
  'clients:view': 'Vedere clienti',
  'invoice:manage': 'Emettere fatture',
  'invoice:view': 'Vedere fatture',
  'logs:view': 'Vedere logs',
  'orders:edit-price': 'Modificare prezzi/sconti',
  'orders:manage': 'Inserire/modificare comande',
  'orders:view': 'Vedere comande',
  'products:manage': 'Gestire articoli',
  'products:view': 'Vedere articoli',
  'receipts:manage': 'Emettere documenti/pagamenti',
  'receipts:view': 'Vedere documenti emessi',
  'reservations:manage': 'Inserire/modificare prenotazioni',
  'reservations:view': 'Vedere prenotazioni',
  'roles:manage': 'Gestire ruoli',
  'roles:view': 'Vedere ruoli',
  'rooms:manage': 'Gestire sale',
  'rooms:view': 'Vedere sale',
  'settings:manage': 'Modificare impostazioni',
  'settings:view': 'Vedere impostazioni',
  'stats:manage': 'Gestire statistiche',
  'stats:view': 'Vedere statistiche',
  'subcategories:manage': 'Gestire sottocategorie',
  'subcategories:view': 'Vedere sottocategorie',
  'sync:manage': 'Gestire sincronizzazione',
  'tables:manage': 'Gestire tavoli',
  'tables:view': 'Vedere tavoli',
  'users:manage': 'Gestire utenti e permessi',
  'users:view': 'Vedere utenti',
};

const PERMISSION_GROUP_LABELS = {
  categories: 'Categorie',
  clients: 'Clienti',
  dashboard: 'Landing page',
  invoice: 'Fatture',
  logs: 'Logs',
  orders: 'Comande',
  products: 'Articoli',
  receipts: 'Cassa e documenti',
  reservations: 'Prenotazioni',
  roles: 'Ruoli',
  rooms: 'Sale',
  settings: 'Impostazioni',
  stats: 'Statistiche',
  status: 'Stato sistema',
  subcategories: 'Sottocategorie',
  sync: 'Sincronizzazione',
  tables: 'Tavoli',
  users: 'Utenti',
};

const permissionLabel = (action) => PERMISSION_LABELS[action] || action;
const permissionGroup = (action) => String(action || '').split(':')[0] || 'altro';
const SUPERADMIN_ROLE = 'superadmin';
const isSuperAdminItem = (item) => (
  String(item?.role?.name || item?.name || '').toLowerCase() === SUPERADMIN_ROLE
);
const isSuperAdminPermission = (permission) => (
  String(permission?.action || '').toLowerCase().startsWith(`${SUPERADMIN_ROLE}:`)
);
const manageableUsers = (data) => (Array.isArray(data) ? data.filter(user => !isSuperAdminItem(user)) : []);
const assignableRoles = (data) => (Array.isArray(data) ? data.filter(role => !isSuperAdminItem(role)) : []);
const manageablePermissions = (data) => (
  Array.isArray(data) ? data.filter(permission => !isSuperAdminPermission(permission)) : []
);
const isUserActive = (user) => user?.isActive !== false;

const Utenti = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const handleAlert = useCallback((msg, type) => {
    showSnackbar(msg, type);
  }, [showSnackbar]);

  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({ name: '', surname: '', email: '', password: '', role: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionDetails, setPermissionDetails] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(new Set());
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsSaving, setPermissionsSaving] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [visibleCategoryIds, setVisibleCategoryIds] = useState(new Set());
  const [categoryVisibilityLoading, setCategoryVisibilityLoading] = useState(false);
  const [categoryVisibilitySaving, setCategoryVisibilitySaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    getAllUtenti(token, tenant)
      .then(data => setUsers(manageableUsers(data)))
      .catch(err => {
        console.error("Errore nell'API getAllUtenti:", err);
        handleAlert(err.message || "Errore caricamento utenti", "error");
      });

    getAllRuoli(token, tenant)
      .then(data => setRoles(assignableRoles(data)))
      .catch(err => console.error("Errore getAllRuoli:", err));

    getAllPermissions(token, tenant)
      .then(data => setPermissions(manageablePermissions(data)))
      .catch(err => {
        console.error("Errore getAllPermissions:", err);
        handleAlert(err.message || "Errore caricamento permessi", "error");
      });

    getAllCategorie(token, tenant)
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(err => handleAlert(err.message || 'Errore caricamento categorie', 'error'));
  }, [handleAlert]);

  useEffect(() => {
    if (selectedUser) {
      if (isSuperAdminItem(selectedUser)) {
        setSelectedUser(null);
        return;
      }
      setFormData({
        name: selectedUser.name,
        surname: selectedUser.surname,
        email: selectedUser.email,
        password: '',
        role: selectedUser.role?.id || ''
      });
    } else {
      setFormData({ name: '', surname: '', email: '', password: '', role: '' });
      setPermissionDetails(null);
      setSelectedPermissionIds(new Set());
    }
  }, [selectedUser]);

  useEffect(() => {
    if (!selectedUser?.id) return;

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    let active = true;

    setPermissionsLoading(true);
    getUserPermissions(selectedUser.id, token, tenant)
      .then(data => {
        if (!active) return;
        setPermissionDetails(data);
        setSelectedPermissionIds(new Set((data?.userPermissions || []).map(permission => Number(permission.id))));
      })
      .catch(err => {
        if (!active) return;
        setPermissionDetails(null);
        setSelectedPermissionIds(new Set());
        handleAlert(err.message || "Errore caricamento permessi utente", "error");
      })
      .finally(() => {
        if (active) setPermissionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [handleAlert, selectedUser?.id]);

  useEffect(() => {
    if (!selectedUser?.id || categories.length === 0) {
      setVisibleCategoryIds(new Set());
      return;
    }

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    let active = true;
    setCategoryVisibilityLoading(true);
    getUserCategoryVisibility(selectedUser.id, token, tenant)
      .then(data => {
        if (!active) return;
        const ids = data?.configured
          ? data.visibleCategoryIds
          : categories.map(category => category.id);
        setVisibleCategoryIds(new Set((ids || []).map(Number)));
      })
      .catch(err => {
        if (active) handleAlert(err.message || 'Errore caricamento visibilita categorie', 'error');
      })
      .finally(() => {
        if (active) setCategoryVisibilityLoading(false);
      });
    return () => { active = false; };
  }, [categories, handleAlert, selectedUser?.id]);

  const rolePermissionIds = useMemo(() => new Set(
    (permissionDetails?.rolePermissions || []).map(permission => Number(permission.id))
  ), [permissionDetails]);

  const userPermissionIds = useMemo(() => new Set(
    (permissionDetails?.userPermissions || []).map(permission => Number(permission.id))
  ), [permissionDetails]);

  const permissionsByGroup = useMemo(() => {
    return (permissions || []).reduce((groups, permission) => {
      const group = permissionGroup(permission.action);
      if (!groups[group]) groups[group] = [];
      groups[group].push(permission);
      return groups;
    }, {});
  }, [permissions]);

  const togglePermission = (permissionId) => {
    setSelectedPermissionIds(prev => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  };

  const resetPermissionDraft = () => {
    setSelectedPermissionIds(new Set((permissionDetails?.userPermissions || []).map(permission => Number(permission.id))));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const handleAddUser = async () => {
    if (!formData.name || !formData.surname || !formData.email || !formData.role) return;

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const existingInactiveUser = users.find(user => (
      !isUserActive(user)
      && String(user.email).trim().toLowerCase() === formData.email.trim().toLowerCase()
    ));

    if (existingInactiveUser) {
      setStatusAction({
        user: existingInactiveUser,
        isActive: true,
        reactivationData: {
          name: formData.name,
          surname: formData.surname,
          email: formData.email,
          password: formData.password || 'Password456!',
          roleId: Number(formData.role),
        },
      });
      return;
    }

    try {
      await registerUtente({
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        password: formData.password || 'Password456!',
        roleId: formData.role,
      }, tenant);

      const refreshedUsers = await getAllUtenti(token, tenant);
      setUsers(manageableUsers(refreshedUsers));
      setFormData({ name: '', surname: '', email: '', password: '', role: '' });

    } catch (error) {
      handleAlert(t("user_alert.error_registration") + error.message, "error");
    }
  };

  const refreshUsers = async () => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const refreshedUsers = await getAllUtenti(token, tenant);
    setUsers(manageableUsers(refreshedUsers));
  };

  const requestStatusChange = (user, isActive) => {
    setStatusAction({ user, isActive, reactivationData: null });
  };

  const confirmStatusChange = async () => {
    if (!statusAction?.user) return;

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const { user, isActive, reactivationData } = statusAction;

    setStatusSaving(true);
    try {
      if (reactivationData) {
        await updateUtente(user.id, reactivationData, token, tenant);
      }
      await setUtenteActive(user.id, isActive, token, tenant);
      await refreshUsers();
      setSelectedUser(null);
      setFormData({ name: '', surname: '', email: '', password: '', role: '' });
      setStatusAction(null);
      handleAlert(isActive ? 'Utente riattivato' : 'Utente disattivato', 'success');
    } catch (error) {
      handleAlert(error.message || 'Errore aggiornamento stato utente', 'error');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const selectedRole = roles.find(r => r.id === parseInt(formData.role));

    if (!selectedRole) {
      alert(t("invalidRoleSelected"))

      return;
    }

    const updatePayload = {
      name: formData.name,
      surname: formData.surname,
      email: formData.email,
      roleId: selectedRole.id
    };

    if (formData.password.trim() !== '') {
      updatePayload.password = formData.password;
    }

    try {
      await updateUtente(selectedUser.id, updatePayload, token, tenant);
      const refreshedUsers = await getAllUtenti(token, tenant);
      setUsers(manageableUsers(refreshedUsers));
      setSelectedUser(null);
      setFormData({ name: '', surname: '', email: '', password: '', role: '' });

    } catch (err) {
      console.error("Errore updateUtente:", err);
      handleAlert(t("user_alert.error_updateUser") + (err.message || ""), "error");
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedUser?.id) return;

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    setPermissionsSaving(true);
    try {
      const result = await updateUserPermissions(
        selectedUser.id,
        [...selectedPermissionIds],
        token,
        tenant
      );
      setPermissionDetails(result);
      setSelectedPermissionIds(new Set((result?.userPermissions || []).map(permission => Number(permission.id))));
      handleAlert("Permessi utente aggiornati", "success");
    } catch (err) {
      handleAlert(err.message || "Errore salvataggio permessi", "error");
    } finally {
      setPermissionsSaving(false);
    }
  };

  const toggleCategoryVisibility = (categoryId) => {
    setVisibleCategoryIds(current => {
      const next = new Set(current);
      if (next.has(Number(categoryId))) next.delete(Number(categoryId));
      else next.add(Number(categoryId));
      return next;
    });
  };

  const handleSaveCategoryVisibility = async () => {
    if (!selectedUser?.id) return;
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    setCategoryVisibilitySaving(true);
    try {
      const result = await saveUserCategoryVisibility(
        selectedUser.id,
        [...visibleCategoryIds],
        token,
        tenant
      );
      setVisibleCategoryIds(new Set((result.visibleCategoryIds || []).map(Number)));
      handleAlert('Visualizzazione categorie aggiornata', 'success');
    } catch (err) {
      handleAlert(err.message || 'Errore salvataggio categorie', 'error');
    } finally {
      setCategoryVisibilitySaving(false);
    }
  };

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 } }}>
        <Typography>{t('user_management.title')}</Typography>
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('user_management.add_edit_title')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('user_management.name')}
                  name="name"
                  fullWidth
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('user_management.surname')}
                  name="surname"
                  fullWidth
                  value={formData.surname}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('user_management.email')}
                  name="email"
                  fullWidth
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('user_management.password')}
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  fullWidth
                  value={formData.password}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="role-label">{t('user_management.role')}</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formData.role}
                    label={t('user_management.role')}
                    onChange={handleInputChange}
                  >
                    {roles.map(role => (
                      <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {selectedUser ? ( //quando seleziono un utente, fai apparire il bottone cancel
                <>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleUpdateUser}
                      sx={{ height: '100%' }}
                    >
                      {t('user_management.update_button')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      onClick={(() => {
                        setSelectedUser(null);
                        setFormData({ name: '', surname: '', email: '', password: '', role: '' });
                      })}
                      sx={{ height: '100%' }}
                    >
                      {t('cancel_button')}
                    </Button>
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleAddUser}
                    sx={{ height: '100%' }}
                  >
                    {t('user_management.add_button')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {selectedUser && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Permessi utente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.name} {selectedUser.surname} - {selectedUser.email}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip label={`Ruolo: ${permissionDetails?.role?.name || selectedUser.role?.name || '-'}`} color="secondary" />
                  <Chip label={`${selectedPermissionIds.size} permessi diretti`} variant="outlined" />
                </Stack>
              </Stack>

              <Alert severity="info" sx={{ mb: 2 }}>
                I permessi ereditati dal ruolo sono gia attivi e non si possono togliere da qui. Qui si gestiscono solo i permessi diretti del singolo utente.
              </Alert>

              {permissionsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography>Caricamento permessi...</Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {Object.entries(permissionsByGroup).map(([group, groupPermissions]) => (
                    <Box key={group} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Typography fontWeight={800} sx={{ mb: 1 }}>
                        {PERMISSION_GROUP_LABELS[group] || group}
                      </Typography>
                      <Grid container spacing={1}>
                        {groupPermissions.map(permission => {
                          const permissionId = Number(permission.id);
                          const inherited = rolePermissionIds.has(permissionId);
                          const direct = selectedPermissionIds.has(permissionId);
                          const wasDirect = userPermissionIds.has(permissionId);
                          return (
                            <Grid item xs={12} md={6} key={permission.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={inherited || direct}
                                    disabled={inherited || permissionsSaving}
                                    onChange={() => togglePermission(permissionId)}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2" fontWeight={700}>
                                      {permissionLabel(permission.action)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {inherited ? 'Da ruolo' : direct ? 'Diretto' : wasDirect ? 'Diretto da rimuovere' : permission.action}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ))}
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={resetPermissionDraft} disabled={permissionsSaving}>
                      Annulla modifiche
                    </Button>
                    <Button variant="contained" onClick={handleSavePermissions} disabled={permissionsSaving}>
                      {permissionsSaving ? 'Salvataggio...' : 'Salva permessi'}
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {selectedUser && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6">Visualizzazione categorie</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Scegli le categorie visibili per {selectedUser.name} {selectedUser.surname} nella presa comanda e nel punto cassa.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  size="small"
                  onClick={() => setVisibleCategoryIds(new Set(categories.map(category => Number(category.id))))}
                  disabled={categoryVisibilityLoading || categoryVisibilitySaving}
                >
                  Seleziona tutte
                </Button>
                <Button
                  size="small"
                  onClick={() => setVisibleCategoryIds(new Set())}
                  disabled={categoryVisibilityLoading || categoryVisibilitySaving}
                >
                  Nessuna
                </Button>
              </Stack>
              {categoryVisibilityLoading ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CircularProgress size={20} />
                  <Typography>Caricamento categorie...</Typography>
                </Box>
              ) : (
                <Grid container spacing={1}>
                  {categories.map(category => (
                    <Grid item xs={12} sm={6} key={category.id}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={visibleCategoryIds.has(Number(category.id))}
                            onChange={() => toggleCategoryVisibility(category.id)}
                            disabled={categoryVisibilitySaving}
                          />
                        )}
                        label={category.name}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveCategoryVisibility}
                  disabled={categoryVisibilityLoading || categoryVisibilitySaving}
                >
                  {categoryVisibilitySaving ? 'Salvataggio...' : 'Salva visualizzazione'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('user_management.user_list')}
            </Typography>
            {users.length === 0 ? (
              <Typography>{t('user_management.noUserAvailable')}.</Typography>
            ) : (
              users.map(user => (
                <div key={user.id}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 1, cursor: 'pointer', opacity: isUserActive(user) ? 1 : 0.62 }}
                    onClick={() => setSelectedUser(user)}
                  >
                    <Grid item xs={12} sm={5}>
                      <Typography variant="body1">{user.name} {user.surname}</Typography>
                      <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={user.role?.name} color="secondary" />
                        {!isUserActive(user) && <Chip label="Disattivato" color="default" variant="outlined" />}
                      </Stack>
                    </Grid>
                    <Grid item xs={6} sm={4} sx={{ textAlign: 'right' }}>
                      <Button
                        size="small"
                        color={isUserActive(user) ? 'error' : 'success'}
                        variant={isUserActive(user) ? 'outlined' : 'contained'}
                        disabled={isUserActive(user) && Number(localStorage.getItem('id')) === user.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          requestStatusChange(user, !isUserActive(user));
                        }}
                      >
                        {isUserActive(user) ? 'Disattiva' : 'Riattiva'}
                      </Button>
                    </Grid>
                  </Grid>
                  <Divider sx={{ width: '100%', my: 1 }} />
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </Container>

      <Dialog
        open={Boolean(statusAction)}
        onClose={() => { if (!statusSaving) setStatusAction(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {statusAction?.isActive ? 'Riattiva utente' : 'Disattiva utente'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusAction?.reactivationData
              ? `Esiste gia un utente disattivato con l'email ${statusAction.user.email}. Vuoi riattivarlo usando i dati inseriti? I permessi diretti precedenti verranno rimossi.`
              : statusAction?.isActive
                ? `Vuoi riattivare ${statusAction?.user?.name} ${statusAction?.user?.surname}? I permessi diretti precedenti verranno rimossi.`
                : `Vuoi disattivare ${statusAction?.user?.name} ${statusAction?.user?.surname}? Non potra piu accedere finche non verra riattivato.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusAction(null)} disabled={statusSaving}>Annulla</Button>
          <Button
            variant="contained"
            color={statusAction?.isActive ? 'success' : 'error'}
            onClick={confirmStatusChange}
            disabled={statusSaving}
          >
            {statusSaving ? 'Salvataggio...' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
};

export default Utenti;
